<template>
  <div class="pp-tab">
    <div class="pp-header">
      <div class="pp-header-text">
        <div class="pp-eyebrow">Payments</div>
        <h1 class="pp-title">Patient payments</h1>
        <p class="pp-lede">
          Every payment you've collected through Teamio, and whether it landed in Open Dental.
          Payments write in automatically. <strong>Confirming…</strong> just means we haven't
          double-checked it yet — that clears on its own within a few minutes. Only
          <strong>Not in OD</strong> needs a look.
        </p>
      </div>
      <div class="pp-header-actions">
        <button type="button" class="pp-btn" :disabled="loading" @click="load">
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }" aria-hidden="true"></i>
          {{ loading ? 'Refreshing…' : 'Refresh' }}
        </button>
        <button type="button" class="pp-btn pp-btn--primary" :disabled="!visibleRows.length" @click="exportCsv">
          <i class="fa fa-download" aria-hidden="true"></i> Export CSV
        </button>
      </div>
    </div>

    <PaymentStatCards
      :stats="stats"
      :channel-mix="channelMix"
      :range-label="rangeLabel"
      :loading="loading"
    />

    <!-- Only surfaced when something actually needs attention, so it stays a
         signal rather than permanent furniture. -->
    <div v-if="stats.attnCount > 0" class="pp-alert">
      <span class="pp-alert-icon"><i class="fa fa-exclamation-triangle" aria-hidden="true"></i></span>
      <div class="pp-alert-body">
        <div class="pp-alert-title">
          {{ stats.attnCount }} payment{{ stats.attnCount === 1 ? '' : 's' }}
          {{ stats.attnCount === 1 ? "hasn't" : "haven't" }} reached Open Dental
        </div>
        <div class="pp-alert-sub">
          {{ stats.attnCount === 1 ? 'This one is' : 'These are' }} past the point where
          {{ stats.attnCount === 1 ? 'it' : 'they' }} should have landed. Check Open Dental — if
          {{ stats.attnCount === 1 ? "it's" : "they're" }} genuinely missing, post
          {{ stats.attnCount === 1 ? 'it' : 'them' }} manually to keep the ledger balanced.
        </div>
      </div>
      <button type="button" class="pp-alert-action" @click="syncFilter = 'issues'">Show only these</button>
    </div>

    <div class="pp-card">
      <PaymentFilters
        :presets="presets"
        :active-preset="activePreset"
        :from="from"
        :to="to"
        :search="search"
        :sync-filter="syncFilter"
        :any-filter="anyFilter"
        @preset="applyPreset"
        @update:from="from = $event"
        @update:to="to = $event"
        @update:search="search = $event"
        @update:syncFilter="syncFilter = $event"
        @clear="clearFilters"
      />

      <div v-if="loading" class="pp-loading">
        <b-spinner small /> <span>Loading payments…</span>
      </div>

      <template v-else>
        <b-alert v-if="truncated" show variant="warning" class="pp-truncated">
          <i class="fa fa-exclamation-triangle mr-2" aria-hidden="true"></i>
          Showing the first {{ payments.length }} payments for this range — the total below covers
          only those. Narrow the dates to see them all.
        </b-alert>

        <PaymentTable
          :rows="visibleRows"
          :total="stats.total"
          :count="stats.count"
          :foot-note="footNote"
          :empty-title="emptyTitle"
          :empty-sub="emptySub"
          :format-date-time="formatPaidAt"
        />
      </template>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapActions } from 'vuex';
import moment from 'moment-timezone';
import mixin from '../../../../mixin.js';
import PaymentStatCards from './PaymentStatCards.vue';
import PaymentFilters from './PaymentFilters.vue';
import PaymentTable from './PaymentTable.vue';
import { buildPaymentsCsv, paymentsCsvFilename, sumCharged, isCollected, isPartialApproval, isOdProblem } from '../../../../utils/paymentsCsv';
import { parseViewPaymentQuery } from '../../../../utils/actionCenterPaymentLink';

// 'all' deliberately sends a far-past `from` rather than omitting the range.
// Omitting it drops the backend onto its legacy path, which sorts by CreatedAt
// (when the LINK was made) instead of PaidAt — the exact ordering bug this
// report exists to avoid.
const ALL_TIME_START = '2000-01-01';

export default {
  name: 'PatientPaymentsTab',
  components: { PaymentStatCards, PaymentFilters, PaymentTable },
  mixins: [mixin],

  props: {
    siteId: { type: String, required: true },
    timezone: { type: String, default: '' },
  },

  data() {
    return {
      payments: [],
      loading: false,
      truncated: false,
      from: '',
      to: '',
      search: '',
      syncFilter: 'all',
      presets: [
        { key: 'today', label: 'Today' },
        { key: 'yesterday', label: 'Yesterday' },
        { key: 'last7', label: 'Last 7 days' },
        { key: 'last30', label: 'Last 30 days' },
        { key: 'all', label: 'All time' },
      ],
    };
  },

  computed: {
    // Search and sync are applied client-side over the fetched range. Amounts
    // and every stat derive from THIS list, so the headline total always agrees
    // with the rows on screen.
    visibleRows() {
      const q = this.search.trim().toLowerCase();
      return this.payments.filter((p) => {
        if (this.syncFilter === 'synced' && p.OdSyncStatus !== 'synced') return false;
        // A voided charge is not an "issue" — it was reversed at the
        // processor, so Open Dental holding no record of it is correct.
        // "Issues" means rows a human must act on — not rows still inside our
        // own confirmation window. Same predicate the NEEDS ATTENTION card uses,
        // so clicking "Show only these" can never disagree with the count above it.
        if (this.syncFilter === 'issues' && !isOdProblem(p)) return false;
        if (!q) return true;
        return [p.PatientName, p.PatNum, p.MoolahTransactionId]
          .some((f) => f != null && String(f).toLowerCase().includes(q));
      });
    },

    // Rows that represent money actually collected. A charge voided at the
    // terminal after capture is still LISTED (see the table) but is not a
    // collection, so it drives none of the numbers below — counting it would
    // over-state the day the office reconciles against.
    collectedRows() {
      return this.visibleRows.filter(isCollected);
    },

    stats() {
      const rows = this.collectedRows;
      // 'pending' is NOT a problem — it is our own confirmation cadence, not
      // Open Dental's state.
      //
      // The OD write is dispatched within a second of the charge and the exe
      // applies it in seconds (measured 2026-09-15, Summerbrook: card captured
      // 20:39:44Z, queue drained by 20:40:05Z). But reconcileOdPayments runs on
      // rate(5 minutes) and won't look at a row until it clears the grace
      // window, so the chip trails the real write by minutes. Counting that
      // window as NEEDS ATTENTION told a brand-new practice their first payment
      // hadn't landed — 24 minutes after it had. Only 'unconfirmed' (past grace,
      // still missing) and 'failed' (the write threw) are real.
      const attn = rows.filter((r) => isOdProblem(r));
      const confirming = rows.filter((r) => r.OdSyncStatus === 'pending');
      const synced = rows.filter((r) => r.OdSyncStatus === 'synced').length;
      const voided = this.visibleRows.filter((r) => !isCollected(r));
      return {
        total: sumCharged(rows),
        count: rows.length,
        synced,
        // 100% on an empty range reads better than 0% — there is nothing unsynced.
        syncedPct: rows.length ? Math.round((synced / rows.length) * 100) : 100,
        attnCount: attn.length,
        attnTotal: sumCharged(attn),
        // Reported separately so the progress bar can explain the gap between
        // `synced` and `count` without borrowing the alarm colour.
        confirmingCount: confirming.length,
        confirmingTotal: sumCharged(confirming),
        voidedCount: voided.length,
        voidedTotal: sumCharged(voided),
        // Partial approvals ARE in the total — real money, just less than the
        // office asked for. Surfaced so the shortfall is stated rather than
        // left for someone to discover by subtracting.
        partialCount: rows.filter(isPartialApproval).length,
        partialShortfall: Math.round(rows.filter(isPartialApproval).reduce(
          (n, r) => n + (parseFloat(r.TotalCharged) - parseFloat(r.CapturedAmount)), 0,
        ) * 100) / 100,
      };
    },

    channelMix() {
      const defs = [
        { key: 'terminal', label: 'In-office', color: '#0f7259' },
        { key: 'link', label: 'Payment link', color: '#f59e0b' },
        { key: 'unknown', label: 'Other', color: '#9ca4a2' },
      ];
      return defs
        .map((d) => ({
          ...d,
          amount: sumCharged(this.collectedRows.filter((r) => (r.channel || 'unknown') === d.key)),
        }))
        .filter((d) => d.amount > 0);
    },

    activePreset() {
      if (!this.from || !this.to) return null;
      const today = this.siteToday();
      if (this.from === today && this.to === today) return 'today';
      if (this.from === this.siteToday(-1) && this.to === this.siteToday(-1)) return 'yesterday';
      if (this.from === this.siteToday(-6) && this.to === today) return 'last7';
      if (this.from === this.siteToday(-29) && this.to === today) return 'last30';
      if (this.from === ALL_TIME_START && this.to === today) return 'all';
      return null;
    },

    rangeLabel() {
      const found = this.presets.find((p) => p.key === this.activePreset);
      return found ? found.label : 'Custom range';
    },

    anyFilter() {
      return !!(this.search || this.syncFilter !== 'all');
    },

    footNote() {
      const parts = [];
      if (this.stats.count) {
        if (this.stats.attnCount) {
          parts.push(this.money(this.stats.attnTotal) + ' of this total has not reached Open Dental');
        } else if (this.stats.confirmingCount) {
          // Not a warning — the write is already on its way. Say what is
          // happening instead of claiming everything is confirmed (it isn't
          // yet) or that something is wrong (it isn't).
          parts.push(this.money(this.stats.confirmingTotal) + ' of this total is still being confirmed — check back in a few minutes');
        } else {
          parts.push('Every payment in this range is confirmed in Open Dental');
        }
      }
      // Say WHY the total is lower than the rows on screen add up to. An
      // unexplained gap in a reconciliation report reads as a bug.
      if (this.stats.voidedCount) {
        parts.push(`${this.stats.voidedCount} voided payment${this.stats.voidedCount === 1 ? '' : 's'} `
          + `(${this.money(this.stats.voidedTotal)}) excluded from the total`);
      }
      // The opposite direction from a void: the money IS in the total, but less
      // of it arrived than was asked for. Left unsaid, the office reconciles a
      // correct total against a balance that didn't move as far as expected.
      if (this.stats.partialCount) {
        parts.push(`${this.stats.partialCount} partially approved payment${this.stats.partialCount === 1 ? '' : 's'} `
          + `— ${this.money(this.stats.partialShortfall)} was not collected`);
      }
      return parts.join(' · ');
    },

    // Distinguish "nothing collected" from "your filters hid it" — an empty
    // table shouldn't read as a broken report.
    emptyTitle() {
      if (this.payments.length && !this.visibleRows.length) return 'No payments match';
      return this.from === this.to ? 'No payments collected on this date' : 'No payments in this range';
    },

    emptySub() {
      if (this.payments.length && !this.visibleRows.length) return 'Try clearing the search or the sync filter.';
      return 'Try a wider date range.';
    },
  },

  watch: {
    // Typing in a date input fires per keystroke; coalesce so a half-typed year
    // doesn't trigger four requests.
    from() { this.debouncedLoad(); },
    to() { this.debouncedLoad(); },
  },

  created() {
    // Action Center "Payment received → View payment" links here with
    // ?tab=payments&from=YYYY-MM-DD&to=YYYY-MM-DD&search=<patient> (src/utils/actionCenterPaymentLink.js)
    // so the payment is on screen. Anything else opens on Today, as always.
    const linked = parseViewPaymentQuery(this.$route && this.$route.query);
    if (linked) {
      this.from = linked.from;
      this.to = linked.to;
      this.search = linked.search;
      return;
    }
    this.applyPreset('today');
  },

  beforeDestroy() {
    clearTimeout(this._reloadTimer);
  },

  methods: {
    ...mapActions('teamio', ['getOdSyncStatusList']),

    money(n) {
      const v = Number(n) || 0;
      return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    // Today in the PRACTICE's timezone, not the browser's. A front desk opening
    // this at 8pm must see today's takings, and a browser in another zone must
    // not shift the day.
    siteToday(offsetDays = 0) {
      const m = this.timezone ? moment.tz(this.timezone) : moment();
      return m.add(offsetDays, 'days').format('YYYY-MM-DD');
    },

    formatPaidAt(v) {
      return this.formatDateTimeWitTz(v, this.timezone);
    },

    applyPreset(key) {
      const today = this.siteToday();
      const map = {
        today: [today, today],
        yesterday: [this.siteToday(-1), this.siteToday(-1)],
        last7: [this.siteToday(-6), today],
        last30: [this.siteToday(-29), today],
        all: [ALL_TIME_START, today],
      };
      const [from, to] = map[key] || map.today;
      const unchanged = from === this.from && to === this.to;
      this.from = from;
      this.to = to;
      // The watchers only fire on an actual change; re-clicking the active chip
      // should still refresh rather than sit there doing nothing.
      if (unchanged) this.load();
    },

    clearFilters() {
      this.search = '';
      this.syncFilter = 'all';
    },

    debouncedLoad() {
      clearTimeout(this._reloadTimer);
      this._reloadTimer = setTimeout(() => {
        if (this.from && this.to && this.from <= this.to) this.load();
      }, 400);
    },

    async load() {
      if (!this.siteId || !this.from || !this.to) return;
      this.loading = true;
      try {
        // limit 200 (the backend max) rather than the default 50: this is a
        // reconciliation total, so silently dropping rows would make the sum
        // wrong. `truncated` surfaces the cap when a range exceeds it.
        const res = await this.getOdSyncStatusList({
          siteID: this.siteId,
          from: this.from,
          to: this.to,
          limit: 200,
        });
        this.payments = (res && res.payments) || [];
        this.truncated = !!(res && res.truncated);
      } catch (err) {
        console.error('Error loading patient payments:', err);
        this.payments = [];
        this.truncated = false;
      } finally {
        this.loading = false;
      }
    },

    exportCsv() {
      const csv = buildPaymentsCsv({
        payments: this.visibleRows,
        formatPaid: (v) => (v ? this.formatPaidAt(v) : ''),
        channelLabel: (c) => ({ terminal: 'In-office', link: 'Payment link' }[c] || '—'),
        syncLabel: (s) => ({
          synced: 'Synced to OD',
          pending: 'Confirming',
          unconfirmed: 'Not in OD',
          failed: 'Write failed',
        }[s] || s || ''),
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = paymentsCsvFilename({ from: this.from, to: this.to });
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

.pp-header {
  display: flex;
  align-items: flex-end;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.pp-header-text { flex: 1; min-width: 260px; }

.pp-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: $pp-brand;
  margin-bottom: 8px;
}

.pp-title {
  margin: 0;
  font-family: $pp-font-display;
  font-weight: 800;
  font-size: 34px;
  letter-spacing: -0.03em;
  line-height: 1.05;
  color: $pp-text;
}

.pp-lede {
  margin: 8px 0 0;
  font-size: 15px;
  color: $pp-text-muted;
  max-width: 660px;
  line-height: 1.5;

  strong { font-weight: 700; color: $pp-text-strong; }
}

.pp-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pp-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 42px;
  padding: 0 16px;
  border-radius: $pp-radius-sm;
  border: 1px solid $pp-border-strong;
  background: $pp-surface;
  color: $pp-text-strong;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;

  &:hover:not(:disabled) { background: $pp-surface-sunken; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }

  &--primary {
    background: $pp-brand;
    border-color: $pp-brand;
    color: #fff;

    &:hover:not(:disabled) { background: $pp-brand-deep; border-color: $pp-brand-deep; }
  }
}

.pp-alert {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 13px 16px;
  background: $pp-red-subtle;
  border: 1px solid $pp-red-border;
  border-radius: 13px;
  margin-bottom: 16px;
}

.pp-alert-icon {
  width: 34px;
  height: 34px;
  border-radius: $pp-radius-sm;
  background: $pp-red;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
}

.pp-alert-body { flex: 1; }
.pp-alert-title { font-weight: 700; font-size: 14px; color: $pp-red-text; }
.pp-alert-sub { font-size: 12.5px; color: #b04a4e; margin-top: 1px; }

.pp-alert-action {
  border: 0;
  background: none;
  font-size: 13px;
  font-weight: 700;
  color: $pp-red-text;
  text-decoration: underline;
  cursor: pointer;
  flex: none;
}

.pp-card {
  background: $pp-surface;
  border: 1px solid $pp-border;
  border-radius: 16px;
  box-shadow: $pp-shadow;
  overflow: hidden;
}

.pp-loading {
  padding: 40px 20px;
  text-align: center;
  color: $pp-text-muted;
  font-size: 14px;
}

.pp-truncated {
  margin: 14px 18px 0;
  padding: 10px 14px;
  font-size: 13px;
}
</style>
